import { computed, ref, InjectionKey } from "@vue/composition-api";
import {
  defaultOfficialDataList,
  formKeyLabelList,
  CharmsKeyLabelTypes,
  ImagesKeyLabelTypes,
  MoviesKeyLabelTypes,
  isRequiredOfficialData,
  requiredOfficialDataLabelList,
  OfficialEditingFormType,
  OfficialContentsNameType,
  EditingOfficialData,
  EditingOfficialContent,
  EditableOfficialContentKeyType,
  OfficialError,
  hasForbiddenUrl,
  OfficialWarn,
  warnUrlMessage,
  hasWarnUrl,
  YOUTUBE_EMBED_HTML_PATTERN,
  YOUTUBE_URL_PATTERN
} from "../official";
import { MoveOrderType } from "@/components/Organisms/InputField/types";
import { useValidate } from "@/helpers/hooks/useValidate";
import {
  DATE_WITH_TIME_FORMATS,
  validateDateFormat
} from "@/helpers/date-helper";
import { getRandomId } from "@/helpers/math-helper";
import { useComponentInstance } from "./useComponentInstance";

import { checkPickUpApproved } from "../contract-helper";

export type OfficialStore = ReturnType<typeof useOfficial>;

export const OFFICIAL_STORE_KEY: InjectionKey<OfficialStore> = Symbol(
  "OFFICIAL_STORE_KEY"
);

import {
  getOfficials,
  getOfficial,
  createOfficial,
  updateOfficial,
  confirmOfficial,
  unconfirmOfficial
} from "@/api/index";
import { ApiRequest, ApiResponse, OfficialContentType } from "@/api/types";
import { useRequest } from "@/helpers/hooks/useRequest";

const { validate } = useValidate();

// TODO: データ構造複雑なのでここに残す（牧野）

/**
 * Officialの編集情報、エラー情報をグローバルで保持する
 */
export const useOfficial = () => {
  const root = useComponentInstance();

  /** ================================================================================
   * バージョン一覧
   ================================================================================ */
  // バージョン一覧情報
  const [{ data: officialsData }, runGetOfficials] = useRequest(() =>
    getOfficials()
  );

  /** ================================================================================
   * バージョン詳細
   ================================================================================ */
  const queryOfficialDetailId = root.$route.query["id"];

  // 現在選択しているバージョンのID
  const officialDetailId = ref<number | undefined>(
    typeof queryOfficialDetailId === "string"
      ? Number(root.$route.query["id"])
      : undefined
  );

  /**
   * 現在のバージョンの値を変更する
   * @param value 最新のバージョンのID
   */
  const setOfficialDetailId = (value: number) => {
    officialDetailId.value = value;
  };

  // バージョン詳細情報
  const [{ data: officialDetailData }, runGetOfficialDetail] = useRequest(
    typeof officialDetailId.value != undefined
      ? () => getOfficial({ official_id: officialDetailId.value as number })
      : undefined
  );

  // 掲載依頼中のバージョンかどうか
  const isPendingVersion = computed(
    () => officialDetailData.value?.approval_status === "pending"
  );

  /**
   * バージョンの切り替え
   * @param official 新たに選択されたバージョン
   * @returns void
   */
  const switchOfficialVersion = async (officialId: number) => {
    setOfficialDetailId(officialId);
    await runGetOfficialDetail();

    if (officialDetailData.value) {
      resetAllOfficialError();

      await initSetEditingOfficial(officialDetailData.value);
      root.$router
        .replace({
          name: "OfficialEdit",
          query: { id: String(officialId) }
        })
        .catch(err => err);
    }
  };

  // 契約リスト
  const contracts = computed<ApiResponse.Contract[]>(() => {
    const partnerUser: ApiResponse.PartnerUser | undefined =
      root.$store.state.domains.user.partnerUser;

    const contracts = partnerUser?.partner.contracts || [];
    return contracts;
  });

  /**
   * 「注目の企業画像」を表示するか
   * NOTE: 契約上入稿できない場合は非表示
   */
  const isShowTopHeadImage = computed<boolean>(() => {
    if (!officialEditingForm.value) {
      return false;
    }

    const selectedContracts = contracts.value.find(
      contract => contract.id === officialEditingForm.value?.contract_id
    );
    return !!selectedContracts && checkPickUpApproved(selectedContracts);
  });

  /** ================================================================================
   * バージョン一覧・詳細情報のロード
   ================================================================================ */
  // バージョン一覧・詳細のロードが完了したか
  const officialLoaded = ref(false);

  /**
   * バージョン一覧・詳細のロード
   */
  const loadOfficial = async () => {
    officialLoaded.value = false;

    await runGetOfficials();
    const officials = officialsData.value?.officials;

    // 既存のバージョンがある場合
    if (officials && officials.length) {
      if (!officialDetailId.value) {
        // 最新のバージョンを取得して編集フォームに入れる
        const latestVersion = officials[0];
        setOfficialDetailId(latestVersion.id);
        officialEditingForm.value.submit_type = "sameDate";
      }

      await runGetOfficialDetail();
      if (officialDetailData.value) {
        await initSetEditingOfficial(officialDetailData.value);
      }
    }
    // 既存のバージョンがない場合
    else {
      await createNewOfficialForm();
    }
    officialLoaded.value = true;
  };

  /**
   * バージョン一覧・最新の詳細のロード
   */
  const loadLatestOfficial = async () => {
    setOfficialDetailId(0);
    await loadOfficial();
  };

  /** ================================================================================
   * フォームの値更新
   ================================================================================ */
  // フロントのフォーム情報
  const officialEditingForm = ref<OfficialEditingFormType>({});
  // 現在選択しているバージョンが編集されたかどうか
  const isOfficialEdited = ref<boolean>(false);
  const uncroppedOfficialImage = ref<string>("");
  const uncroppedTopHeadImage = ref<string>("");

  /**
   * 画像URLをbase64形式に変換
   * @param url 画像URL
   * @returns string base64形式画像データ
   */
  const getImageBase64 = async (url: string) => {
    let response = undefined;
    try {
      response = await fetch(url);
    } catch (err) {
      return "";
    }
    const contentType = response.headers.get("content-type");
    if (contentType === null) return "";
    const isContentTypeImage = new RegExp("^image/.*").test(contentType);
    if (!isContentTypeImage) return "";

    const arrayBuffer = await response.arrayBuffer();
    const APPLY_MAX = 1024;
    let encodedStr = "";
    // ArrayBufferの中身を1024バイトに区切って少しずつ文字列にしていく
    for (var i = 0; i < arrayBuffer.byteLength; i += APPLY_MAX) {
      new Uint8Array(arrayBuffer.slice(i, i + APPLY_MAX)).forEach(
        (byte: number) => {
          encodedStr += String.fromCharCode(byte);
        }
      );
    }
    let base64String = btoa(encodedStr);
    return `data:${contentType};base64,${base64String}`;
  };

  /**
   * 編集フォームにバージョン詳細データを格納
   * @param officialDetail バージョン詳細データ
   * @returns void
   */
  const initSetEditingOfficial = async (
    officialDetail: ApiResponse.OfficialDetail
  ) => {
    isOfficialEdited.value = false;

    // 編集可能な項目を編集フォームに追加
    let tmpObj: OfficialEditingFormType = {};

    const missingRequiredOfficialData = requiredOfficialDataLabelList
      .filter(label => {
        return officialDetail.official_data.every(data => data.label !== label);
      })
      .map(label => {
        return {
          id: undefined,
          tmpId: getRandomId(),
          label: label,
          text: "",
          sort_order: undefined,
          required: true
        };
      });

    const sortedOfficialDataList = officialDetail.official_data
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(officialData => {
        return {
          id: officialData.id,
          label: officialData.label,
          text: officialData.text,
          required: isRequiredOfficialData(officialData.label)
        };
      });

    tmpObj.official_data = [
      ...sortedOfficialDataList,
      ...missingRequiredOfficialData
    ];

    tmpObj.contract_id = officialDetail.contract_id;
    tmpObj.official_description = officialDetail.official_description;
    tmpObj.official_image = officialDetail.official_image_url
      ? await getImageBase64(officialDetail.official_image_url)
      : "";
    tmpObj.top_head_image = officialDetail.top_head_image_url
      ? await getImageBase64(officialDetail.top_head_image_url)
      : "";

    tmpObj.charms = await Promise.all(
      officialDetail.official_contents
        .filter(v => v.content_type === "content_type_1")
        .map(async v => {
          const base64_image1 = v.image1_url
            ? await getImageBase64(v.image1_url)
            : "";
          return {
            id: v.id,
            content_type: v.content_type,
            title: v.title,
            description1: v.description1,
            image1: base64_image1,
            movie1: v.movie1,
            position: 0
          };
        })
    );
    tmpObj.images = await Promise.all(
      officialDetail.official_contents
        .filter(v => v.content_type === "content_type_3")
        .map(async v => {
          const base64_image1 = v.image1_url
            ? await getImageBase64(v.image1_url)
            : "";
          return {
            id: v.id,
            content_type: v.content_type,
            title: v.title,
            description1: v.description1,
            image1: base64_image1,
            movie1: v.movie1,
            position: 0
          };
        })
    );
    tmpObj.movies = officialDetail.official_contents
      .filter(v => v.content_type === "content_type_7")
      .map(v => {
        return {
          id: v.id,
          content_type: v.content_type,
          title: v.title,
          description1: v.description1,
          image1: v.image1_url,
          movie1: v.movie1,
          position: 0
        };
      });
    const isValidStartAt = new Date(officialDetail.start_at) > new Date();
    tmpObj.start_at = isValidStartAt ? officialDetail.start_at : "";
    tmpObj.submit_type = isValidStartAt ? "specifiedDate" : "sameDate";
    officialEditingForm.value = tmpObj;
  };

  /**
   * フォーム新規作成
   */
  const createNewOfficialForm = async () => {
    let tmpObj: OfficialEditingFormType = {};

    tmpObj.official_data = defaultOfficialDataList.map(officialData => {
      return {
        id: undefined,
        tmpId: getRandomId(),
        label: officialData.label,
        text: "",
        sort_order: undefined,
        required: officialData.required
      };
    });
    tmpObj.contract_id = 0;
    tmpObj.official_description = "";
    tmpObj.official_image = "";
    tmpObj.top_head_image = "";
    tmpObj.charms = [];
    tmpObj.images = [];
    tmpObj.movies = [];
    tmpObj.start_at = "";
    tmpObj.submit_type = "sameDate";

    officialEditingForm.value = tmpObj;
  };

  /**
   * 編集フォームの値をセットする際の処理
   * @param key 編集フォームのkey
   * @param value 更新後のvalue
   * @returns void
   */
  const onSetForm = (
    key: {
      mainKey: keyof OfficialEditingFormType;
      id?: number;
      subKey?: EditableOfficialContentKeyType | "text" | "label";
    },
    value: string
  ) => {
    if (!officialEditingForm.value) return;
    if (!isOfficialEdited.value) {
      isOfficialEdited.value = true;
    }

    // フォームの値を更新
    switch (key.mainKey) {
      case "charms":
      case "images":
      case "movies":
        setFormOfficialContent(
          key as {
            mainKey: OfficialContentsNameType;
            id: number;
            subKey: EditableOfficialContentKeyType;
          },
          value
        );
        break;
      case "official_data":
        setFormOfficialData(
          key as {
            mainKey: "official_data";
            id: number;
            subKey?: "label" | "text";
          },
          value
        );
        break;
      default:
        setFormText(key.mainKey, value);
        break;
    }

    // 新しい値でバリデーションし直す
    resetOfficialError(key);
    resetOfficialWarn(key);
    immediateValidate(key, value);
  };

  /**
   * 値更新時に即時行うバリデーション
   * @param key 編集フォームのkey
   * @param value 更新後のvalue
   * @returns void
   */
  const immediateValidate = (
    key: {
      mainKey: keyof OfficialEditingFormType;
      id?: number;
      subKey?: EditableOfficialContentKeyType | "text" | "label";
    },
    value: string
  ) => {
    switch (key.mainKey) {
      case "contract_id":
      case "top_head_image":
      case "official_image":
        break;
      case "official_description":
      case "start_at":
      case "official_data":
      case "movies":
        validateTextField(key, value);
        break;
      case "charms":
      case "images":
        if (key.subKey === "image1") return;
        validateTextField(key, value);
        break;
    }
  };

  /**
   * 編集フォームの値(テキスト)をセット
   * @param key 編集フォームのkey
   * @param value 更新後のvalue
   * @returns void
   */
  const setFormText = (key: keyof OfficialEditingFormType, value: string) => {
    (officialEditingForm.value[key] as string) = value;
  };

  /**
   * 編集フォームの値(official_data)をセット
   * @param key 編集フォームのkey
   * @param value 更新後のvalue
   * @returns void
   */
  const setFormOfficialData = (
    key: {
      mainKey: "official_data";
      id: number;
      subKey?: "label" | "text";
    },
    value: string
  ) => {
    const updateTarget = officialEditingForm.value[key.mainKey]?.find(
      v => (v.id || v.tmpId) === key.id
    );
    if (!updateTarget) return;

    if (!key.subKey) {
      updateTarget.text = value;
    } else {
      updateTarget[key.subKey] = value;
    }
  };

  /**
   * 編集フォームの値(official_contents)をセット
   * @param key 編集フォームのkey情報
   * @param value 更新後のvalue
   * @returns void
   */
  const setFormOfficialContent = (
    key: {
      mainKey: OfficialContentsNameType;
      id: number;
      subKey: EditableOfficialContentKeyType;
    },
    value: string
  ) => {
    const updateTarget = (officialEditingForm.value[
      key.mainKey
    ] as EditingOfficialContent[]).find(v => (v.id || v.tmpId) === key.id);

    if (!updateTarget) return;
    updateTarget[key.subKey] = value;
  };

  const setUncroppedContentImage = (
    key: {
      mainKey: keyof OfficialEditingFormType;
      id?: number;
    },
    value: string
  ) => {
    const updateTarget = (officialEditingForm.value[
      key.mainKey
    ] as EditingOfficialContent[]).find(v => (v.id || v.tmpId) === key.id);

    if (!updateTarget) return;
    updateTarget.uncroppedImage = value;
  };

  // メイン画像をリセット
  const resetOfficialImage = () => {
    setFormText("official_image", "");
  };

  // 注目の企業画像をリセット
  const resetTopHeadImage = () => {
    setFormText("top_head_image", "");
  };

  const resetOfficialContentImage = (key: {
    mainKey: OfficialContentsNameType;
    id: number;
    subKey: EditableOfficialContentKeyType;
  }) => {
    setFormOfficialContent(key, "");
  };

  /** ================================================================================
   * エラー・バリデーション
   ================================================================================ */
  /**
   * OfficialDataのidからラベルを返却
   * @param id OfficialDataのid
   * @returns string OfficialDataのlabel
   */
  const getOfficialDataLabelFromId = (id: number) => {
    return officialEditingForm.value.official_data?.find(
      v => v.id === id || v.tmpId === id
    )?.label;
  };

  // フォームのエラー
  const officialErrors = ref<OfficialError[]>([]);
  const officialWarns = ref<OfficialWarn[]>([]);

  /**
   * 項目名+エラー詳細のエラーメッセージ一覧
   */
  const mergedErrorMessageList = computed(() => {
    if (!officialEditingForm.value) {
      return [];
    }
    return officialErrors.value.map(error => getMergedErrorMessage(error));
  });

  /**
   * 項目名+エラー詳細のエラーメッセージを返却
   * @param error エラー内容
   * @return string: エラーメッセージ
   */
  const getMergedErrorMessage = (error: OfficialError): string => {
    let label: string = "";
    if (error.key?.id) {
      label = getOfficialDataLabelFromId(error.key.id) || "";
    }

    switch (error.key.mainKey) {
      case "contract_id":
      case "official_description":
      case "official_image":
      case "top_head_image":
        return `${formKeyLabelList.official_data.label}/${
          formKeyLabelList[error.key.mainKey].label
        }：${error.message}`;
      case "start_at":
        return `${formKeyLabelList[error.key.mainKey].label}：${error.message}`;
      case "charms":
        return `${formKeyLabelList[error.key.mainKey].section_name.label}/${
          formKeyLabelList[error.key.mainKey][
            error.key.subKey as CharmsKeyLabelTypes
          ].label
        }：${error.message}`;
      case "images":
        return `${formKeyLabelList[error.key.mainKey].section_name.label}/${
          formKeyLabelList[error.key.mainKey][
            error.key.subKey as ImagesKeyLabelTypes
          ].label
        }：${error.message}`;
      case "movies":
        return `${formKeyLabelList[error.key.mainKey].section_name.label}/${
          formKeyLabelList[error.key.mainKey][
            error.key.subKey as MoviesKeyLabelTypes
          ].label
        }：${error.message}`;
      case "official_data":
        return `${formKeyLabelList[error.key.mainKey].label}/${label}：${
          error.message
        }`;
      default:
        return "";
    }
  };

  /**
   * フォームのエラーを格納
   * @param key エラー箇所を示すキー
   * @returns void
   */
  const setOfficialErrors = (
    key: {
      mainKey: keyof OfficialEditingFormType;
      id?: number;
      subKey?: EditableOfficialContentKeyType | "text" | "label";
    },
    message?: string
  ) => {
    const nextErrors = officialErrors.value.filter(error => {
      return (
        JSON.stringify(Object.entries(error.key).sort()) !==
        JSON.stringify(Object.entries(key).sort())
      );
    });

    if (message) {
      nextErrors.push({ key, message });
    }
    officialErrors.value = nextErrors;
  };

  /**
   * 画面に表示するエラーメッセージを返却
   * @param key エラー箇所を示すキー
   * @returns string エラーメッセージ
   */
  const getOfficialErrorMessage = (key: {
    mainKey: string;
    id?: number;
    subKey?: EditableOfficialContentKeyType | "text" | "label";
  }) => {
    const error = officialErrors.value.find(error => {
      return (
        JSON.stringify(Object.entries(error.key).sort()) ===
        JSON.stringify(Object.entries(key).sort())
      );
    });

    return error?.message;
  };

  /**
   * フォームの警告を格納
   * @param key 警告箇所を示すキー
   * @returns void
   */
  const setOfficialWarns = (
    key: {
      mainKey: keyof OfficialEditingFormType;
      id?: number;
      subKey?: EditableOfficialContentKeyType | "text" | "label";
    },
    message?: string
  ) => {
    const nextWarns = officialWarns.value.filter(warn => {
      return (
        JSON.stringify(Object.entries(warn.key).sort()) !==
        JSON.stringify(Object.entries(key).sort())
      );
    });

    if (message) nextWarns.push({ key, message });
    officialWarns.value = nextWarns;
  };

  /**
   * 画面に表示する警告メッセージを返却
   * @param key 警告箇所を示すキー
   * @returns string 警告メッセージ
   */
  const getOfficialWarnMessage = (key: {
    mainKey: string;
    id?: number;
    subKey?: EditableOfficialContentKeyType | "text" | "label";
  }) => {
    const warn = officialWarns.value.find(warn => {
      return (
        JSON.stringify(Object.entries(warn.key).sort()) ===
        JSON.stringify(Object.entries(key).sort())
      );
    });

    return warn?.message;
  };

  /**
   * 画像サイズのバリデーションチェック
   */
  const validateAllImageSize = () => {
    // メイン画像
    const isOfficialImageUncropped =
      !officialEditingForm.value.official_image && uncroppedOfficialImage.value;
    if (isOfficialImageUncropped) {
      setOfficialErrors(
        { mainKey: "official_image" },
        "画像登録には切り抜く必要があります"
      );
    }

    // 注目の企業画像
    const isTopHeadImageUncropped =
      !officialEditingForm.value.top_head_image && uncroppedTopHeadImage.value;
    if (isTopHeadImageUncropped) {
      setOfficialErrors(
        { mainKey: "top_head_image" },
        "画像登録には切り抜く必要があります"
      );
    }

    // 企業の魅力
    officialEditingForm.value.charms?.forEach(charm => {
      const isImageUncropped = !charm.image1 && !!charm.uncroppedImage;
      if (isImageUncropped) {
        setOfficialErrors(
          { mainKey: "charms", id: charm.id || charm.tmpId, subKey: "image1" },
          "画像登録には切り抜く必要があります"
        );
      }
    });

    // イメージギャラリー
    officialEditingForm.value.images?.forEach(image => {
      const isImageUncropped = !image.image1 && !!image.uncroppedImage;
      if (isImageUncropped) {
        setOfficialErrors(
          { mainKey: "images", id: image.id || image.tmpId, subKey: "image1" },
          "画像登録には切り抜く必要があります"
        );
      }
    });
  };

  /**
   * 下書き保存に必要なバリデーション
   */
  const validateForDraft = () => {
    // テキスト項目のバリデーション: 説明文
    validateTextField(
      { mainKey: "official_description" },
      officialEditingForm.value.official_description
    );
    // テキスト項目のバリデーション: 掲載依頼日時
    validateTextField(
      { mainKey: "start_at" },
      officialEditingForm.value.start_at
    );
    // テキスト項目のバリデーション: 基本情報項目
    officialEditingForm.value.official_data?.forEach(data => {
      validateTextField(
        { mainKey: "official_data", id: data.id || data.tmpId, subKey: "text" },
        data.text
      );
    });
    // テキスト項目のバリデーション: 企業の魅力
    officialEditingForm.value.charms?.forEach(data => {
      validateTextField(
        { mainKey: "charms", id: data.id || data.tmpId, subKey: "title" },
        data.title
      );
      validateTextField(
        {
          mainKey: "charms",
          id: data.id || data.tmpId,
          subKey: "description1"
        },
        data.description1
      );
    });
    // テキスト項目のバリデーション: イメージギャラリー
    officialEditingForm.value.images?.forEach(data => {
      validateTextField(
        { mainKey: "images", id: data.id || data.tmpId, subKey: "title" },
        data.title
      );
      validateTextField(
        {
          mainKey: "images",
          id: data.id || data.tmpId,
          subKey: "description1"
        },
        data.description1
      );
    });
    // テキスト項目のバリデーション: オリジナルムービー
    officialEditingForm.value.movies?.forEach(data => {
      validateTextField(
        { mainKey: "movies", id: data.id || data.tmpId, subKey: "movie1" },
        data.movie1
      );
      validateTextField(
        { mainKey: "movies", id: data.id || data.tmpId, subKey: "title" },
        data.title
      );
      validateTextField(
        {
          mainKey: "movies",
          id: data.id || data.tmpId,
          subKey: "description1"
        },
        data.description1
      );
    });

    // 画像の切り抜きチェック
    validateAllImageSize();
  };

  /**
   * テキスト項目をバリデーションチェック
   * @param key エラー箇所を示すキー
   * @param text テキスト内容
   * @returns void
   */
  const validateTextField = (
    key: {
      mainKey: keyof OfficialEditingFormType;
      id?: number;
      subKey?: EditableOfficialContentKeyType | "text" | "label";
    },
    text?: string
  ) => {
    if (!text) return;
    // 設立日: フォーマット
    const label = key.id ? getOfficialDataLabelFromId(key.id) : undefined;
    if (key.mainKey === "official_data" && label === "設立日") {
      if (!text) {
        setOfficialErrors(key, "設立日を設定してください");
        return;
      }
    }

    // イメージギャラリー 説明文: 70文字以内
    if (key.mainKey === "images" && key.subKey === "description1") {
      const error = validate(text, {
        maxLength: 70
      })[0]?.message;

      if (error) {
        setOfficialErrors(key, error);
        return;
      }
    }

    // オリジナルムービー 動画URL: YouTube形式
    if (key.mainKey === "movies" && key.subKey === "movie1") {
      if (
        !YOUTUBE_EMBED_HTML_PATTERN.test(text) &&
        !YOUTUBE_URL_PATTERN.test(text)
      ) {
        setOfficialErrors(
          key,
          "YouTubeのURL または YouTubeの埋め込みタグを入力してください"
        );
        return;
      } else {
        return;
      }
    }

    // 更新希望日時: フォーマット
    if (
      key.mainKey === "start_at" &&
      officialEditingForm.value.submit_type === "specifiedDate"
    ) {
      const validated = validateDateFormat(text, DATE_WITH_TIME_FORMATS);
      if (!validated) {
        setOfficialErrors(
          key,
          "有効な日時を設定してください（例: 2022年01月01日 12時00分）"
        );
        return;
      }
    }

    if (hasForbiddenUrl(text)) {
      setOfficialErrors(key, "掲載できないURLが含まれています");
      return;
    }
    if (hasWarnUrl(text)) {
      setOfficialWarns(key, warnUrlMessage);
    }
  };

  const validateRequiredField = (
    key: {
      mainKey: keyof OfficialEditingFormType;
      id?: number;
      subKey?: EditableOfficialContentKeyType | "label" | "text";
    },
    value?: string | number
  ) => {
    const error = validate(value, {
      required: true
    })[0]?.message;
    if (error) {
      setOfficialErrors(key, error);
      return;
    }
  };

  /**
   * 全必須項目が入力されているかバリデーション
   */
  const validateAllRequiredFields = () => {
    validateRequiredField(
      { mainKey: "contract_id" },
      officialEditingForm.value.contract_id
    );
    validateRequiredField(
      { mainKey: "official_description" },
      officialEditingForm.value.official_description
    );

    if (officialEditingForm.value.submit_type !== "sameDate") {
      validateRequiredField(
        { mainKey: "start_at" },
        officialEditingForm.value.start_at
      );
    }

    // メイン画像: 未切り抜きの画像すらない場合のみ、必須バリデーションを出す
    // ※ 未切り抜きの画像がある場合は「切り抜いてください」バリデーション
    if (
      !uncroppedOfficialImage.value &&
      !officialEditingForm.value.official_image
    ) {
      validateRequiredField(
        { mainKey: "official_image" },
        officialEditingForm.value.official_image
      );
    }

    officialEditingForm.value.official_data?.forEach(data => {
      validateRequiredField(
        {
          mainKey: "official_data",
          id: data.id || data.tmpId,
          subKey: "label"
        },
        data.label
      );
      validateRequiredField(
        {
          mainKey: "official_data",
          id: data.id || data.tmpId,
          subKey: "text"
        },
        data.text
      );
    });

    officialEditingForm.value.charms?.forEach(data => {
      validateRequiredField(
        {
          mainKey: "charms",
          id: data.id || data.tmpId,
          subKey: "title"
        },
        data.title
      );
      validateRequiredField(
        {
          mainKey: "charms",
          id: data.id || data.tmpId,
          subKey: "description1"
        },
        data.description1
      );
    });

    officialEditingForm.value.images?.forEach(data => {
      // イメージギャラリー画像: 未切り抜きの画像すらない場合のみ、必須バリデーションを出す
      // ※ 未切り抜きの画像がある場合は「切り抜いてください」バリデーション
      if (!data.uncroppedImage && !data.image1) {
        validateRequiredField(
          {
            mainKey: "images",
            id: data.id || data.tmpId,
            subKey: "image1"
          },
          data.image1
        );
      }
    });

    officialEditingForm.value.movies?.forEach(data => {
      validateRequiredField(
        {
          mainKey: "movies",
          id: data.id || data.tmpId,
          subKey: "title"
        },
        data.title
      );
      validateRequiredField(
        {
          mainKey: "movies",
          id: data.id || data.tmpId,
          subKey: "movie1"
        },
        data.movie1
      );
    });
  };

  /**
   * 掲載依頼に必要なバリデーション
   */
  const validateForSubmit = () => {
    validateForDraft();

    // 必須項目バリデーション
    validateAllRequiredFields();

    // 更新希望日時: 3営業日より先
    if (officialEditingForm.value.submit_type === "sameDate") return; // 「即日指定」の場合はバリデーション不要
    const error = validate(officialEditingForm.value.start_at, {
      businessDaysLater: 3
    })[0]?.message;

    if (error) {
      setOfficialErrors({ mainKey: "start_at" }, error);
      return;
    }
  };

  /**
   * エラーの有無
   * @returns boolean エラーがあるか
   */
  const hasOfficialErrors = computed(() => {
    return officialErrors.value.length > 0;
  });

  /**
   * 指定したkeyに紐づくエラーをリセット
   */
  const resetOfficialError = (key: {
    mainKey: keyof OfficialEditingFormType;
    id?: number;
    subKey?: EditableOfficialContentKeyType | "text" | "label";
  }) => {
    officialErrors.value = officialErrors.value.filter(error => {
      return (
        JSON.stringify(Object.entries(error.key).sort()) !==
        JSON.stringify(Object.entries(key).sort())
      );
    });
  };

  /**
   * 全てのエラーをリセット
   */
  const resetAllOfficialError = () => {
    officialErrors.value = [];
  };

  /**
   * 指定したkeyに紐づく警告をリセット
   */
  const resetOfficialWarn = (key: {
    mainKey: keyof OfficialEditingFormType;
    id?: number;
    subKey?: EditableOfficialContentKeyType | "text" | "label";
  }) => {
    officialWarns.value = officialWarns.value.filter(warn => {
      return (
        JSON.stringify(Object.entries(warn.key).sort()) !==
        JSON.stringify(Object.entries(key).sort())
      );
    });
  };

  /** ================================================================================
   * 企業情報 - 任意項目関連
   ================================================================================ */
  /**
   * 企業情報 - 任意項目の追加
   */
  const addOfficialOptionalData = () => {
    if (!isOfficialEdited.value) {
      isOfficialEdited.value = true;
    }
    if (!officialEditingForm.value.official_data) {
      officialEditingForm.value.official_data = [];
    }
    officialEditingForm.value.official_data.push({
      tmpId: getRandomId(),
      label: "",
      text: ""
    });
  };

  /**
   * 企業情報 - 任意項目の削除
   * @param id 削除するコンテンツのid
   * @returns
   */
  const deleteOfficialOptionalData = (id: number) => {
    if (!officialEditingForm.value.official_data) return;

    if (!isOfficialEdited.value) {
      isOfficialEdited.value = true;
    }

    const targetOfficialContentIndex = officialEditingForm.value.official_data.findIndex(
      v => v.id === id || v.tmpId === id
    );
    officialEditingForm.value.official_data.splice(
      targetOfficialContentIndex,
      1
    );
  };

  /**
   * 企業情報 - 任意項目の順番変更
   * @param content_type コンテンツタイプ
   * @param index 移動するコンテンツのインデックス
   * @param order 昇順 or 降順
   * @returns void
   */
  const changeOrderOfficialOptionalData = (
    index: number,
    order: MoveOrderType
  ) => {
    if (!officialEditingForm.value.official_data) return;

    if (!isOfficialEdited.value) {
      isOfficialEdited.value = true;
    }

    if (order === "raise") {
      officialEditingForm.value.official_data.splice(
        index - 1,
        2,
        officialEditingForm.value.official_data[index],
        officialEditingForm.value.official_data[index - 1]
      );
    } else {
      officialEditingForm.value.official_data.splice(
        index,
        2,
        officialEditingForm.value.official_data[index + 1],
        officialEditingForm.value.official_data[index]
      );
    }
  };

  /** ================================================================================
   * official_contents(企業の魅力・イメージギャラリー・オリジナルムービー)関連
   ================================================================================ */
  // 各official_contentsと、バックエンドで保持するタイプの対応
  const contentTypeMapping: {
    [key in OfficialContentsNameType]: OfficialContentType;
  } = {
    charms: "content_type_1",
    images: "content_type_3",
    movies: "content_type_7"
  };

  /**
   * official_contentsの追加
   * @param content_type コンテンツタイプ
   */
  const addOfficialContent = (content_type: OfficialContentsNameType) => {
    if (!isOfficialEdited.value) {
      isOfficialEdited.value = true;
    }
    if (!officialEditingForm.value[content_type]) {
      officialEditingForm.value[content_type] = [];
    }
    const officialContentRef = officialEditingForm.value[
      content_type
    ] as EditingOfficialContent[];

    officialContentRef.push({
      tmpId: getRandomId(),
      content_type: contentTypeMapping[content_type],
      title: "",
      description1: "",
      image1: "",
      movie1: "",
      position: officialContentRef.length
    });
  };

  /**
   * official_contentsの削除
   * @param content_type コンテンツタイプ
   * @param id 削除するコンテンツのid
   * @returns
   */
  const deleteOfficialContent = (
    content_type: OfficialContentsNameType,
    id: number
  ) => {
    if (!officialEditingForm.value[content_type]) return;

    if (!isOfficialEdited.value) {
      isOfficialEdited.value = true;
    }

    const officialContentRef = officialEditingForm.value[
      content_type
    ] as EditingOfficialContent[];

    // 削除するofficial_contentに紐づくエラーは削除
    officialErrors.value = officialErrors.value.filter(error => {
      return error.key.id !== id;
    });

    const targetOfficialContentIndex = officialContentRef.findIndex(
      v => v.id === id || v.tmpId === id
    );
    officialContentRef.splice(targetOfficialContentIndex, 1);
  };

  /**
   * official_contentsの順番変更
   * @param content_type コンテンツタイプ
   * @param index 移動するコンテンツのインデックス
   * @param order 昇順 or 降順
   * @returns void
   */
  const changeOrderOfficialContents = (
    content_type: OfficialContentsNameType,
    index: number,
    order: MoveOrderType
  ) => {
    if (!officialEditingForm.value[content_type]) return;

    if (!isOfficialEdited.value) {
      isOfficialEdited.value = true;
    }

    const officialContentRef = officialEditingForm.value[
      content_type
    ] as ApiRequest.OfficialContent[];

    if (order === "raise") {
      officialContentRef.splice(
        index - 1,
        2,
        officialContentRef[index],
        officialContentRef[index - 1]
      );
    } else {
      officialContentRef.splice(
        index,
        2,
        officialContentRef[index + 1],
        officialContentRef[index]
      );
    }
  };

  /** ================================================================================
   * API関連
   ================================================================================ */
  /**
   * バージョン新規作成APIのパラメータ
   * @param form 編集フォーム
   * @returns APIパラメータ
   */
  const createOfficialParam = (
    form: OfficialEditingFormType
  ): ApiRequest.CreateOfficial => {
    return {
      official_description: form.official_description as string,
      official_image: form.official_image as string,
      top_head_image: isShowTopHeadImage.value
        ? (form.top_head_image as string)
        : "",
      contract_id: form.contract_id as number,
      official_data: [
        ...(form.official_data ? officialDataParam(form.official_data) : [])
      ],
      official_contents: [
        ...(!form.charms
          ? []
          : form.charms.map((charm, charmIndex) => {
              return {
                ...charm,
                position: charmIndex
              };
            })),
        ...(!form.images
          ? []
          : form.images?.map((image_gallery, imageIndex) => {
              return {
                ...image_gallery,
                position: imageIndex
              };
            })),
        ...(!form.movies
          ? []
          : form.movies?.map((movie, movieIndex) => {
              return {
                ...movie,
                movie1: getEmbedMovieIframe(movie.movie1),
                position: movieIndex
              };
            }))
      ]
    };
  };

  const officialDataParam = (
    officialDataArray: EditingOfficialData[]
  ): ApiRequest.OfficialData[] => {
    return officialDataArray
      .filter(officialData => {
        // 任意項目のタイトル or 項目内容が空の場合、その項目は削除
        // NOTE: 必須項目に空白がある場合、バリデーションで弾かれるのでここに到達しない
        return !!officialData.label && !!officialData.text;
      })
      .map((officialData, index) => {
        const { tmpId, required, ...remainObj } = officialData;
        return {
          ...remainObj,
          sort_order: index
        };
      });
  };

  /**
   * 埋め込み動画HTMLを返却
   * @param movie 動画URL入力値
   * @returns string 埋め込み動画HTML
   */
  const getEmbedMovieIframe = (movie: string) => {
    if (YOUTUBE_EMBED_HTML_PATTERN.test(movie)) {
      return movie;
    }
    if (YOUTUBE_URL_PATTERN.test(movie)) {
      const youtubeIdList = movie.match(YOUTUBE_URL_PATTERN);
      if (!youtubeIdList || !youtubeIdList[1]) return "";
      return (
        '<iframe width="560" height="315" src="https://www.youtube.com/embed/' +
        youtubeIdList[1] +
        '" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>'
      );
    }
    return "";
  };

  /**
   * バージョン更新APIのパラメータ
   * @param form 編集フォーム
   * @returns APIパラメータ
   */
  const updateOfficialParam = (
    form: OfficialEditingFormType
  ): ApiRequest.UpdateOfficial => {
    return {
      id: officialDetailData.value?.id as number,
      ...createOfficialParam(form)
    };
  };

  /**
   * 掲載依頼APIのパラメータ
   * @param form 編集フォーム
   * @returns APIパラメータ
   */
  const confirmOfficialParam = (
    form: OfficialEditingFormType
  ): ApiRequest.ConfirmOfficial => {
    return {
      official_id: officialDetailData.value?.id as number,
      start_at: form.start_at || "",
      comment: form.comment
    };
  };

  // バージョン新規作成
  const [
    { loading: officialCreateLoading, error: officialCreateError },
    runCreateOfficial
  ] = useRequest(() =>
    createOfficial(createOfficialParam(officialEditingForm.value))
  );

  // バージョン更新
  const [
    { loading: officialUpdateLoading, error: officialUpdateError },
    runUpdateOfficial
  ] = useRequest(() =>
    updateOfficial(updateOfficialParam(officialEditingForm.value))
  );

  // 掲載依頼
  const [
    { loading: officialConfirmLoading, error: officialConfirmError },
    runConfirmOfficial
  ] = useRequest(() =>
    confirmOfficial(confirmOfficialParam(officialEditingForm.value))
  );

  // 掲載依頼取り消し
  const [
    { loading: officialUnconfirmLoading, error: officialUnconfirmError },
    runUnconfirmOfficial
  ] = useRequest(() =>
    unconfirmOfficial({ official_id: officialDetailData.value?.id as number })
  );

  const apiRequesting = computed(() => {
    return (
      officialCreateLoading.value ||
      officialUpdateLoading.value ||
      officialConfirmLoading.value ||
      officialUnconfirmLoading.value
    );
  });

  return {
    officialsData,
    runGetOfficials,

    officialDetailData,
    isPendingVersion,
    switchOfficialVersion,
    isShowTopHeadImage,

    officialLoaded,
    loadOfficial,
    loadLatestOfficial,

    officialEditingForm,
    isOfficialEdited,
    uncroppedOfficialImage,
    uncroppedTopHeadImage,
    onSetForm,
    setUncroppedContentImage,
    resetOfficialImage,
    resetTopHeadImage,
    resetOfficialContentImage,

    mergedErrorMessageList,
    getOfficialErrorMessage,
    getOfficialWarnMessage,
    validateForDraft,
    validateTextField,
    validateForSubmit,
    hasOfficialErrors,
    resetOfficialError,
    resetAllOfficialError,

    addOfficialOptionalData,
    deleteOfficialOptionalData,
    changeOrderOfficialOptionalData,

    addOfficialContent,
    deleteOfficialContent,
    changeOrderOfficialContents,

    runCreateOfficial,
    officialCreateError,
    runUpdateOfficial,
    officialUpdateError,
    runConfirmOfficial,
    officialConfirmError,
    runUnconfirmOfficial,
    officialUnconfirmError,

    apiRequesting,

    officialErrors
  };
};
